import React, { useContext } from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { AuthContext } from "../../context/AuthProvider";
import { trackEvent } from "../../helpers/mixpanel";
import {
  CLUMSE_SEARCH_FEE_USD,
  CLUMSE_SEARCH_FEE_MARKETING_HEADLINE,
} from "../../lib/procurementPricing";

const BLACK = "#000000";
const SLATE_800 = "#1e293b";
const WHITE = "#ffffff";
const IND_ORANGE = "#FF5722";

const SCOPE_EXAMPLES = [
  "Quick appliance & plumbing fixes",
  "HVAC, roofing & major trade scopes",
  "Asphalt driveways, porches & full remodels",
];

const INCLUDED_FEATURES = [
  "Free diagnosis and repair cost estimates",
  "Expert advisor guidance",
  "3 bids guaranteed",
];

/** Pay-as-you-go pricing (below Stay in the Loop on `/`). */
export default function HomeProcurementPricingSection({ onSelectPlan }) {
  const { user } = useContext(AuthContext);

  const handleGetStarted = () => {
    trackEvent("Home pricing signup CTA click", { section: "pay_as_you_go" });
    onSelectPlan?.("individual");
  };

  return (
    <PricingOuter>
      <PricingStripSection id="pricing" aria-labelledby="pricing-heading">
        <MainShell>
          <SectionHeader>
            <SectionTitleOnDark id="pricing-heading">
              One Flat Fee. Any Project.
            </SectionTitleOnDark>
            <ValuePropCopy>
              <strong>$0/month. No subscription fees.</strong> Start a free
              diagnosis with Clumse AI. When you're ready for vetted bids, pay a
              single time. It is the exact same flat rate whether you are fixing
              a leaky faucet or converting an entire back porch.
            </ValuePropCopy>
          </SectionHeader>

          <FlatFeeCard aria-labelledby="flat-fee-heading">
            <FeeHeaderStack>
              <FeeEyebrow>Transparent pricing</FeeEyebrow>
              <FeeAmount aria-label={`${CLUMSE_SEARCH_FEE_USD} dollars`}>
                ${CLUMSE_SEARCH_FEE_USD}
              </FeeAmount>
              <FeeHeadline id="flat-fee-heading">
                {CLUMSE_SEARCH_FEE_MARKETING_HEADLINE}
              </FeeHeadline>
            </FeeHeaderStack>
            <CardBody>
              <FeeBody>
                Covers AI scoping, your project blueprint, and matching with{" "}
                <strong>3 vetted local pros</strong>. Covers everything from
                minor appliance repairs to major residential trades. No tiers,
                no hidden lead fees.
              </FeeBody>
              <ScopeList aria-label="Example project types covered">
                {SCOPE_EXAMPLES.map((line) => (
                  <ScopeItem key={line}>
                    <ScopeCheck aria-hidden>✓</ScopeCheck>
                    <ScopeText>{line}</ScopeText>
                  </ScopeItem>
                ))}
              </ScopeList>
            </CardBody>
          </FlatFeeCard>

          <FeaturesBlock aria-labelledby="pricing-included-heading">
            <FeaturesHeading id="pricing-included-heading">
              Included with every search
            </FeaturesHeading>
            <FeaturesGrid>
              {INCLUDED_FEATURES.map((feature) => (
                <FeatureItem key={feature}>
                  <FeatureCheck aria-hidden>✓</FeatureCheck>
                  <FeatureText>{feature}</FeatureText>
                </FeatureItem>
              ))}
            </FeaturesGrid>
          </FeaturesBlock>

          {!user ? (
            <SignupCtaBlock aria-labelledby="pricing-signup-heading">
              <SignupCtaTitle id="pricing-signup-heading">
                Ready to scope your project?
              </SignupCtaTitle>
              <SignupCtaCopy>
                Create a free account and tell Clumse AI about your repair,
                remodel, or maintenance issue. Get a diagnosis and cost estimate
                before you pay anything.
              </SignupCtaCopy>
              <SignupCtaButton type="button" onClick={handleGetStarted}>
                Start free diagnosis
              </SignupCtaButton>
              <SignupCtaFinePrint>
                $0/month · Flat ${CLUMSE_SEARCH_FEE_USD} when you start search ·
                No credit card to start
              </SignupCtaFinePrint>
            </SignupCtaBlock>
          ) : null}
        </MainShell>
      </PricingStripSection>
    </PricingOuter>
  );
}

const SECTION_MAX = "720px";

const PricingOuter = styled.div`
  width: 100%;
  background: ${BLACK};
  box-sizing: border-box;
`;

const MainShell = styled.div`
  position: relative;
  width: 100%;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: clamp(1rem, 4vw, 4rem);
  padding-right: clamp(1rem, 4vw, 4rem);
  box-sizing: border-box;
`;

const PricingStripSection = styled.section`
  position: relative;
  z-index: 1;
  margin-top: 0;
  padding-top: clamp(3.5rem, 6.5vw, 5.25rem);
  padding-bottom: clamp(2.75rem, 4vw, 3.25rem);
  background: transparent;
  border-bottom: 1px solid ${SLATE_800};
  width: 100%;
  box-sizing: border-box;
`;

const SectionHeader = styled.div`
  max-width: ${SECTION_MAX};
  margin: 0 auto 2.5rem;
  text-align: center;
`;

const ValuePropCopy = styled.p`
  margin: 0.85rem auto 0;
  max-width: 42rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.95rem, 1.85vw, 1.1rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 400;

  strong {
    color: ${WHITE};
    font-weight: 600;
  }

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const SectionTitleOnDark = styled.h2`
  margin: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(1.75rem, 3.5vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${WHITE};
  line-height: 1.15;
`;

const FlatFeeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: ${SECTION_MAX};
  margin: 0 auto;
  padding: clamp(2rem, 4vw, 2.75rem) clamp(1.5rem, 4vw, 2.5rem);
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid ${IND_ORANGE};
  border-radius: 0;
  box-shadow: 4px 4px 0 rgba(255, 87, 34, 0.22);
  box-sizing: border-box;
`;

const FeeHeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  text-align: center;
`;

const FeeEyebrow = styled.p`
  margin: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const FeeAmount = styled.div`
  margin: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-weight: 800;
  font-size: clamp(3.25rem, 8vw, 4.5rem);
  color: ${IND_ORANGE};
  line-height: 1;
  letter-spacing: -0.03em;
`;

const FeeHeadline = styled.h3`
  margin: 0;
  max-width: 28rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${WHITE};
`;

const CardBody = styled.div`
  width: 100%;
  max-width: 28rem;
  margin-top: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
`;

const FeeBody = styled.p`
  margin: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.92rem, 1.8vw, 1rem);
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.78);

  strong {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }
`;

const ScopeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ScopeItem = styled.li`
  display: grid;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  column-gap: 0.65rem;
  align-items: start;
`;

const ScopeCheck = styled.span`
  width: 1.25rem;
  flex-shrink: 0;
  color: ${IND_ORANGE};
  font-weight: 700;
  font-size: 0.92rem;
  line-height: 1.45;
  text-align: center;
`;

const ScopeText = styled.span`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.92rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);
  min-width: 0;
`;

const FeaturesBlock = styled.div`
  max-width: ${SECTION_MAX};
  margin: 2.5rem auto 0;
  padding: 1.5rem clamp(1rem, 3vw, 2rem);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0;
  box-sizing: border-box;
`;

const FeaturesHeading = styled.h3`
  margin: 0 0 1.5rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
`;

const FeaturesGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    text-align: left;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
    align-items: start;
  }
`;

const FeatureItem = styled.li`
  display: grid;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  column-gap: 0.5rem;
  align-items: start;
  width: 100%;
  min-width: 0;

  @media ${device.mobile} {
    justify-content: flex-start;
    text-align: left;
  }
`;

const FeatureCheck = styled.span`
  width: 1.25rem;
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.45;
  color: ${IND_ORANGE};
  text-align: center;
`;

const FeatureText = styled.span`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.92rem;
  line-height: 1.45;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  min-width: 0;

  @media ${device.mobile} {
    text-align: left;
  }
`;

const SignupCtaBlock = styled.div`
  max-width: ${SECTION_MAX};
  margin: 2.5rem auto 0;
  padding: 2rem 1.5rem;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 2.5rem 2rem 0;
  }
`;

const SignupCtaTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(1.25rem, 2.5vw, 1.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${WHITE};
  line-height: 1.25;
`;

const SignupCtaCopy = styled.p`
  margin: 0 auto 1.5rem;
  max-width: 32rem;
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.92rem, 1.6vw, 1rem);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.75);
`;

const SignupCtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  min-width: min(100%, 280px);
  padding: 0.9rem 1.75rem;
  border: 2px solid ${WHITE};
  border-radius: 0;
  background: ${IND_ORANGE};
  color: ${WHITE};
  font-family: "Inter", system-ui, sans-serif;
  font-size: clamp(0.95rem, 2vw, 1.05rem);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f44e1b;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SignupCtaFinePrint = styled.p`
  margin: 0.85rem 0 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.01em;
`;
